import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { cerrarMinuta, fetchMinuta } from '@/services/cobranzas.js';
import MinutaHeader from './components/MinutaHeader.jsx';
import { hasPermission } from '@/utils/permissions.js';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

export default function CerrarMinuta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRegistrar = hasPermission(user, 'COBRANZA_REGISTRAR');
  const [minutaData, setMinutaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchMinuta({ id });
        setMinutaData(data);
      } catch (err) {
        setError(getErrorMessage(err, 'No se pudo cargar la minuta.'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const handleClose = async () => {
    setClosing(true);
    setError('');
    try {
      await cerrarMinuta(id, { usuario_id: user?.id || null });
      navigate(`/cobranzas/minuta/${id}`);
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo cerrar la minuta.'));
    } finally {
      setClosing(false);
    }
  };

  const resumenMedios = minutaData?.resumenMedios || [];
  const resumenTotal = resumenMedios.reduce((acc, medio) => acc + Number(medio.total || 0), 0);
  const isClosed = minutaData?.minuta?.estado === 'CERRADA';

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-5">
      <PageHeaderCompact
        title="Cierre de minuta"
        description="Bloquea nuevas cobranzas en la minuta actual."
        action={
          <button
            type="button"
            onClick={() => navigate(`/cobranzas/minuta/${id}`)}
            className="rounded-xl border border-[rgba(var(--border),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)]"
          >
            Volver
          </button>
        }
      />

      <div className="mt-3">
        <MinutaHeader minuta={minutaData?.minuta} />
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-[rgb(var(--muted))]">Cargando resumen...</p>
      ) : (
        <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.85)] p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Resumen por medio de pago</h2>
          <div className="mt-4 space-y-3">
            {resumenMedios.length ? (
              resumenMedios.map((medio) => (
                <div key={medio.medio_pago} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[rgb(var(--fg))]">{medio.medio_pago}</span>
                  <span className="font-semibold text-[rgb(var(--fg))]">
                    {formatCurrency(medio.total)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-[rgba(var(--muted),0.8)]">No hay cobros registrados.</p>
            )}
            <div className="border-t border-[rgba(var(--border),0.6)] pt-3 text-sm font-semibold text-[rgb(var(--fg))]">
              Total recaudado: {formatCurrency(resumenTotal)}
            </div>
          </div>

          {error ? (
            <div className="mt-4 rounded-xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]">
              {error}
            </div>
          ) : null}

          {!canRegistrar ? (
            <div className="mt-4 rounded-xl border border-[rgba(var(--danger,255_99_99),_0.4)] bg-[rgba(var(--danger,255_99_99),_0.15)] p-3 text-sm text-[rgb(var(--danger,255_99_99))]">
              No tenes permisos para cerrar minutas.
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap gap-3">
            {canRegistrar ? (
              <button
                type="button"
                onClick={handleClose}
                disabled={closing || isClosed}
                className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-90 disabled:opacity-50"
              >
                {isClosed ? 'Minuta cerrada' : closing ? 'Cerrando...' : 'Confirmar cierre'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => navigate(`/cobranzas/minuta/${id}`)}
              className="rounded-xl border border-[rgba(var(--border),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)]"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
